import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid, regular } from "@fortawesome/fontawesome-svg-core/import.macro";
import DataTable from "react-data-table-component";
import Select from "react-select";
import AdminDataService from "../services/admin.service";
import ManagerDataService from "../services/manager.service";

// Props: structureId (number). If absent, tries to read from URL (?id=)
const ShowStructure = ({ structureId: propStructureId }) => {
  const { user, isLoading, getAccessTokenSilently } = useAuth0();
  const [structureId, setStructureId] = useState(propStructureId || null);
  const [structureName, setStructureName] = useState("");
  const [habilitation, setHabilitation] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeKey, setActiveKey] = useState("services");
  const tabs = useMemo(() => ([
    { key: "services", title: "Services" },
    { key: "str-prt-ass", title: "Assureurs" },
    { key: "user-service", title: "Utilisateurs" }
  ]), []);

  const [busy, setBusy] = useState(false);
  const [filterText, setFilterText] = useState("");

  // Services state
  const [services, setServices] = useState([]);
  const [svcForm, setSvcForm] = useState({ libelle_service: "" });
  const [svcEditId, setSvcEditId] = useState(null);
  const [showSvcModal, setShowSvcModal] = useState(false);

  // Associations state
  const [assocs, setAssocs] = useState([]);
  const [assForm, setAssForm] = useState({ id_prestation: "", id_assureur: "" });
  const [assEditId, setAssEditId] = useState(null);
  const [showAssModal, setShowAssModal] = useState(false);
  const [prestations, setPrestations] = useState([]);
  const [assureurs, setAssureurs] = useState([]);

  // User-Service state
  const [userServices, setUserServices] = useState([]);
  const [usForm, setUsForm] = useState({ role: "", id_user: "", id_service: "", recette: 0 });
  const [usEditId, setUsEditId] = useState(null);
  const [showUsModal, setShowUsModal] = useState(false);
  const [users, setUsers] = useState([]);

  // Indisponibilité (motif) modal state
  const [showIndisModal, setShowIndisModal] = useState(false);
  const [indisMotif, setIndisMotif] = useState("");
  const [indisRowId, setIndisRowId] = useState(null);

  const [errorMsg, setErrorMsg] = useState("");
  const getErrorMessage = (err) => {
    const backendMsg = err?.response?.data?.message;
    const validation = err?.response?.data?.errors;
    if (backendMsg && Array.isArray(validation) && validation.length) {
      return backendMsg + " - " + validation.map(e => e.msg).join(", ");
    }
    return backendMsg || err?.message || "Erreur inconnue";
  };

  // Resolve structureId from URL if not given
  useEffect(() => {
    if (structureId == null) {
      try {
        const usp = new URLSearchParams(window.location.search);
        const idStr = usp.get("id");
        if (idStr) setStructureId(Number(idStr));
      } catch {}
    }
  }, [structureId]);

  // Fetch habilitation to guard access (mirror HomeAdmin timing)
  useEffect(() => {
    const fetchHabilitation = async () => {
      try {
        const token = await getAccessTokenSilently();
        await new Promise(r => setTimeout(r, 100));
        const response = await ManagerDataService.habilitation(user.email);
        setHabilitation(response.data[0] || {});
      } catch (e) {
        setHabilitation({});
      } finally {
        setLoading(false);
      }
    };
    if (!isLoading && user?.email) fetchHabilitation();
    else if (!isLoading && !user?.email) setLoading(false);
  }, [user?.email, isLoading, getAccessTokenSilently]);

  // Load select options for assoc tab (reload when modal opens and options are missing)
  useEffect(() => {
    const loadOptions = async () => {
      try {
        if (activeKey === 'str-prt-ass' && showAssModal && (!prestations.length || !assureurs.length)) {
          const [resP, resA] = await Promise.all([
            AdminDataService.list("prestations"),
            AdminDataService.list("assureurs"),
          ]);
          setPrestations(Array.isArray(resP.data) ? resP.data : []);
          setAssureurs(Array.isArray(resA.data) ? resA.data : []);
        }
      } catch (err) {
        setErrorMsg(getErrorMessage(err));
      }
    };
    loadOptions();
  }, [activeKey, showAssModal]);

  // Load select options for user-service tab
  useEffect(() => {
    const loadOptions = async () => {
      try {
        if (activeKey === 'user-service' && showUsModal) {
          if (!users.length) {
            const resU = await AdminDataService.list('users');
            setUsers(Array.isArray(resU.data) ? resU.data : []);
          }
          if (!services.length) { // ensure services for this structure are loaded
            await loadServices();
          }
        }
      } catch (err) {
        setErrorMsg(getErrorMessage(err));
      }
    };
    loadOptions();
  }, [activeKey, showUsModal]);

  const loadServices = async () => {
    if (!structureId) return;
    setBusy(true);
    try {
      const res = await AdminDataService.listServicesByStructure(structureId);
      setServices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setErrorMsg(getErrorMessage(err));
    } finally { setBusy(false); }
  };

  const loadAssocs = async () => {
    if (!structureId) return;
    setBusy(true);
    try {
      const res = await AdminDataService.listStrPrtAssByStructure(structureId);
      setAssocs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setErrorMsg(getErrorMessage(err));
    } finally { setBusy(false); }
  };

  const loadUserServices = async () => {
    if (!structureId) return;
    setBusy(true);
    try {
      const res = await AdminDataService.listUserServiceByStructure(structureId);
      setUserServices(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setErrorMsg(getErrorMessage(err));
    } finally { setBusy(false); }
  };

  useEffect(() => {
    if (structureId && habilitation?.admin === 1) {
      loadServices();
      loadAssocs();
      loadUserServices();
    }
  }, [structureId, habilitation?.admin]);

  // Load structure name for banner
  useEffect(() => {
    const loadName = async () => {
      if (!structureId) return;
      try {
        const res = await AdminDataService.get('structures', structureId);
        const row = res.data;
        setStructureName(row?.libelle_structure || "");
      } catch (err) {
        setErrorMsg(getErrorMessage(err));
      }
    };
    if (structureId && habilitation?.admin === 1) loadName();
  }, [structureId, habilitation?.admin]);

  // Services handlers
  const onEditService = (row) => {
    setSvcEditId(row.id);
    setSvcForm({ libelle_service: row.libelle_service || "" });
    setShowSvcModal(true);
  };
  const onDeleteService = async (id) => {
    if (!window.confirm("Supprimer ce service ?")) return;
    setBusy(true);
    try {
      await AdminDataService.delete("services", id);
      await loadServices();
    } catch (err) {
      setErrorMsg(getErrorMessage(err));
    } finally { setBusy(false); }
  };
  const submitService = async (e) => {
    e.preventDefault();
    if (!structureId) return alert("Structure inconnue");
    const email = user?.email;
    if (!email) return alert("Email utilisateur introuvable");
    const lib = (svcForm.libelle_service || "").trim();
    if (!lib) return alert("Veuillez renseigner le libellé du service");
    setBusy(true);
    try {
      if (svcEditId == null) {
        await AdminDataService.createServiceForStructure(structureId, { libelle_service: lib, id_structure: structureId, email });
      } else {
        await AdminDataService.update("services", svcEditId, { libelle_service: lib, id_structure: structureId, email });
        setSvcEditId(null);
      }
      setSvcForm({ libelle_service: "" });
      setShowSvcModal(false);
      await loadServices();
    } catch (err) {
      setErrorMsg(getErrorMessage(err));
    } finally { setBusy(false); }
  };

  const svcColumns = useMemo(() => ([
    { id: "libelle_service", name: "Libellé", selector: r => r.libelle_service, sortable: true, grow: 2 },
    { id: "libelle_structure", name: "Structure", selector: r => r.libelle_structure, sortable: true },
    {
      id: "__actions",
      name: "Actions",
      width: "180px",
      cell: row => (
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-secondary" onClick={() => onEditService(row)} disabled={busy} title="Éditer" aria-label="Éditer"><FontAwesomeIcon icon={regular('pen-to-square')} /></button>
          <button className="btn btn-sm btn-danger" onClick={() => onDeleteService(row.id)} disabled={busy} title="Supprimer" aria-label="Supprimer"><FontAwesomeIcon icon={regular('trash-can')} /></button>
        </div>
      ),
      ignoreRowClick: true,
      button: true
    }
  ]), [busy]);

  const filteredServices = useMemo(() => {
    if (!filterText) return services;
    const q = filterText.toLowerCase();
    return services.filter(s => String(s.libelle_service || "").toLowerCase().includes(q));
  }, [services, filterText]);

  // Associations handlers
  const onEditAssoc = (row) => {
    setAssEditId(row.id);
    setAssForm({ id_prestation: row.id_prestation || "", id_assureur: row.id_assureur || "" });
    setShowAssModal(true);
  };
  const onDeleteAssoc = async (id) => {
    if (!window.confirm("Supprimer cette association ?")) return;
    setBusy(true);
    try {
      await AdminDataService.delete("str-prt-ass", id);
      await loadAssocs();
    } catch (err) {
      setErrorMsg(getErrorMessage(err));
    } finally { setBusy(false); }
  };
  const submitAssoc = async (e) => {
    e.preventDefault();
    if (!structureId) return alert("Structure inconnue");
    const email = user?.email;
    if (!email) return alert("Email utilisateur introuvable");
    const id_prestation = assForm.id_prestation && Number(assForm.id_prestation);
    const id_assureur = assForm.id_assureur && Number(assForm.id_assureur);
    if (!id_prestation || !id_assureur) return alert("Sélectionnez une prestation et un assureur");
    setBusy(true);
    try {
      if (assEditId == null) {
        await AdminDataService.create("str-prt-ass", { id_structure: structureId, id_prestation, id_assureur, email });
      } else {
        await AdminDataService.update("str-prt-ass", assEditId, { id_structure: structureId, id_prestation, id_assureur, email });
        setAssEditId(null);
      }
      setAssForm({ id_prestation: "", id_assureur: "" });
      setShowAssModal(false);
      await loadAssocs();
    } catch (err) {
      setErrorMsg(getErrorMessage(err));
    } finally { setBusy(false); }
  };

  const assColumns = useMemo(() => ([
    { id: "libelle_prestation", name: "Prestation", selector: r => r.libelle_prestation, sortable: true, grow: 2 },
    { id: "libelle_assureur", name: "Assureur", selector: r => r.libelle_assureur, sortable: true, grow: 2 },
    {
      id: "__actions",
      name: "Actions",
      width: "180px",
      cell: row => (
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-secondary" onClick={() => onEditAssoc(row)} disabled={busy} title="Éditer" aria-label="Éditer"><FontAwesomeIcon icon={regular('pen-to-square')} /></button>
          <button className="btn btn-sm btn-danger" onClick={() => onDeleteAssoc(row.id)} disabled={busy} title="Supprimer" aria-label="Supprimer"><FontAwesomeIcon icon={regular('trash-can')} /></button>
        </div>
      ),
      ignoreRowClick: true,
      button: true
    }
  ]), [busy]);

  const filteredAssocs = useMemo(() => {
    if (!filterText) return assocs;
    const q = filterText.toLowerCase();
    return assocs.filter(a =>
      String(a.libelle_prestation || "").toLowerCase().includes(q) ||
      String(a.libelle_assureur || "").toLowerCase().includes(q)
    );
  }, [assocs, filterText]);

  // User-Service handlers
  const onEditUserService = (row) => {
    setUsEditId(row.id);
    setUsForm({
      role: row.role || "",
      id_user: row.id_user || "",
      id_service: row.id_service || "",
      recette: row.role === 'manager' && Number(row.recette) === 1 ? 1 : 0
    });
    setShowUsModal(true);
  };
  const onDeleteUserService = async (id) => {
    if (!window.confirm("Supprimer ce lien utilisateur/service ?")) return;
    setBusy(true);
    try {
      await AdminDataService.deleteUserService(id);
      await loadUserServices();
    } catch (err) {
      setErrorMsg(getErrorMessage(err));
    } finally { setBusy(false); }
  };

  const onToggleIndis = async (row) => {
    try {
      const email = user?.email;
      if (!email) throw new Error('Email utilisateur introuvable');
      if (row.indis == null) {
        setIndisRowId(row.id);
        setIndisMotif("");
        setShowIndisModal(true);
      } else {
        setBusy(true);
        await AdminDataService.closeIndisMngSec(row.indis, { email });
        await loadUserServices();
      }
    } catch (err) {
      const msg = err?.response?.data?.message || getErrorMessage(err);
      setErrorMsg(msg);
    } finally { setBusy(false); }
  };

  const submitOpenIndis = async (e) => {
    e.preventDefault();
    const email = user?.email;
    if (!email) return setErrorMsg('Email utilisateur introuvable');
    if (!indisRowId) return setErrorMsg('Ligne utilisateur/service introuvable');
    const motifTrimmed = (indisMotif || '').trim();
    if (!motifTrimmed) return setErrorMsg('Motif requis (1-30 caractères)');
    const motif = motifTrimmed.slice(0, 30);
    setBusy(true);
    try {
      await AdminDataService.openIndisMngSec({ id_user_service: indisRowId, motif, email });
      setShowIndisModal(false);
      setIndisMotif("");
      setIndisRowId(null);
      await loadUserServices();
    } catch (err) {
      const msg = err?.response?.data?.message || getErrorMessage(err);
      setErrorMsg(msg);
    } finally { setBusy(false); }
  };

  const submitUserService = async (e) => {
    e.preventDefault();
    const email = user?.email; if (!email) return alert("Email utilisateur introuvable");
    const role = (usForm.role || "").trim();
    const id_user = usForm.id_user && Number(usForm.id_user);
    const id_service = usForm.id_service && Number(usForm.id_service);
    const recette = role === 'manager' ? Number(usForm.recette) : 0;
    if (!role || !id_user || !id_service) return alert("Renseignez rôle, utilisateur et service");
    setBusy(true);
    try {
      if (usEditId == null) {
        await AdminDataService.createUserService({ role, id_user, id_service, recette, email });
      } else {
        await AdminDataService.updateUserService(usEditId, { role, id_user, id_service, recette, email });
        setUsEditId(null);
      }
      setUsForm({ role: "", id_user: "", id_service: "", recette: 0 });
      setShowUsModal(false);
      await loadUserServices();
    } catch (err) {
      const msg = err?.response?.data?.message || getErrorMessage(err);
      setErrorMsg(msg);
    } finally { setBusy(false); }
  };

  const usColumns = useMemo(() => ([
    { id: 'id', name: 'ID', selector: r => r.id, sortable: true, width: '80px' },
    { id: 'role', name: 'Rôle', selector: r => r.role, sortable: true },
    { id: 'user', name: 'Utilisateur', selector: r => `${r.nom || ''} ${r.prenom || ''}`.trim(), sortable: true, grow: 2 },
    { id: 'email', name: 'Email', selector: r => r.email, sortable: true, grow: 2 },
    { id: 'service', name: 'Service', selector: r => r.libelle_service, sortable: true, grow: 2 },
    { id: 'recette', name: 'Accès recette', selector: r => (Number(r.recette) === 1 ? 'oui' : 'non'), sortable: true, width: '120px' },
    {
      id: 'statut',
      name: 'Statut',
      width: '120px',
      cell: row => (
        <div className="form-check form-switch m-0">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            checked={row.indis == null}
            onChange={() => onToggleIndis(row)}
            aria-label={row.indis == null ? 'Disponible' : 'Indisponible'}
            title={row.indis == null ? 'Basculer pour rendre indisponible' : 'Basculer pour rendre disponible'}
          />
        </div>
      ),
      ignoreRowClick: true,
      button: true
    }
  ]), [busy]);

  const usRowStyles = useMemo(() => ([
    {
      when: (r) => r.indis != null,
      style: { textDecoration: 'line-through' }
    }
  ]), []);

  const filteredUserServices = useMemo(() => {
    if (!filterText) return userServices;
    const q = filterText.toLowerCase();
    return userServices.filter(u =>
      String(u.role || '').toLowerCase().includes(q) ||
      String(u.nom || '').toLowerCase().includes(q) ||
      String(u.prenom || '').toLowerCase().includes(q) ||
      String(u.email || '').toLowerCase().includes(q) ||
      String(u.libelle_service || '').toLowerCase().includes(q) ||
      (Number(u.recette) === 1 ? 'oui' : 'non').includes(q)
    );
  }, [userServices, filterText]);

  return (
    <div className="col-md-12 mx-auto">
      <div className="row mx-auto" style={{ backgroundColor: "#6f42c1", color: "white", padding: "20px" }}>
        <span style={{ fontSize: "1.5rem" }} className="lead"><FontAwesomeIcon icon={solid('hospital')} size="2x"/> <b>{structureName || (structureId ? `Structure #${structureId}` : 'Structure')}</b></span>
      </div>
      <div className="row mx-auto" style={{ padding: "20px" }}>
        {(loading || habilitation == null) ? null : (
          habilitation?.admin !== 1 ? (
            <div className="mt-4">
              <div className="alert alert-danger d-flex align-items-center" role="alert">
                <span className="me-2"><FontAwesomeIcon icon={solid('triangle-exclamation')} /></span>
                <div><strong>Accès interdit.</strong> Vous n'avez pas les droits nécessaires pour accéder à cette section.</div>
              </div>
            </div>
          ) : (
            <>
              <ul className="nav nav-tabs" role="tablist">
                {tabs.map(t => (
                  <li className="nav-item" role="presentation" key={t.key}>
                    <button className={`nav-link ${activeKey === t.key ? 'active' : ''}`} type="button" role="tab" onClick={() => setActiveKey(t.key)}>{t.title}</button>
                  </li>
                ))}
              </ul>

              <div className="tab-content" style={{ marginTop: 12 }}>
                {activeKey === 'services' && (
                  <>
                    <div className="d-flex align-items-center mb-2 gap-2">
                      <input type="text" className="form-control form-control-sm" style={{ width: 220 }} placeholder="Rechercher..." value={filterText} onChange={(e) => setFilterText(e.target.value)} />
                      <button className="btn btn-sm btn-outline-primary" onClick={loadServices} disabled={busy} title="Rafraîchir" aria-label="Rafraîchir"><FontAwesomeIcon icon={solid('rotate')} /></button>
                      <button className="btn btn-sm btn-primary" onClick={() => { setSvcForm({ libelle_service: '' }); setSvcEditId(null); setShowSvcModal(true); }} disabled={busy} title="Nouveau service" aria-label="Nouveau service"><FontAwesomeIcon icon={solid('plus')} /></button>
                    </div>
                    <DataTable
                      columns={svcColumns}
                      data={filteredServices}
                      dense
                      pagination
                      highlightOnHover
                      progressPending={busy}
                      responsive
                    />
                  </>
                )}
                {activeKey === 'str-prt-ass' && (
                  <>
                    <div className="d-flex align-items-center mb-2 gap-2">
                      <input type="text" className="form-control form-control-sm" style={{ width: 220 }} placeholder="Rechercher..." value={filterText} onChange={(e) => setFilterText(e.target.value)} />
                      <button className="btn btn-sm btn-outline-primary" onClick={loadAssocs} disabled={busy} title="Rafraîchir" aria-label="Rafraîchir"><FontAwesomeIcon icon={solid('rotate')} /></button>
                      <button className="btn btn-sm btn-primary" onClick={() => { setAssForm({ id_prestation: '', id_assureur: '' }); setAssEditId(null); setShowAssModal(true); }} disabled={busy} title="Nouvelle association" aria-label="Nouvelle association"><FontAwesomeIcon icon={solid('plus')} /></button>
                    </div>
                    <DataTable
                      columns={assColumns}
                      data={filteredAssocs}
                      dense
                      pagination
                      highlightOnHover
                      progressPending={busy}
                      responsive
                    />
                  </>
                )}
                {activeKey === 'user-service' && (
                  <>
                    <div className="d-flex align-items-center mb-2 gap-2">
                      <input type="text" className="form-control form-control-sm" style={{ width: 220 }} placeholder="Rechercher..." value={filterText} onChange={(e) => setFilterText(e.target.value)} />
                      <button className="btn btn-sm btn-outline-primary" onClick={loadUserServices} disabled={busy} title="Rafraîchir" aria-label="Rafraîchir"><FontAwesomeIcon icon={solid('rotate')} /></button>
                      <button className="btn btn-sm btn-primary" onClick={() => { setUsForm({ role: '', id_user: '', id_service: '', recette: 0 }); setUsEditId(null); setShowUsModal(true); }} disabled={busy} title="Nouveau lien" aria-label="Nouveau lien"><FontAwesomeIcon icon={solid('plus')} /></button>
                    </div>
                    <DataTable
                      columns={usColumns}
                      data={filteredUserServices}
                      conditionalRowStyles={usRowStyles}
                      dense
                      pagination
                      highlightOnHover
                      progressPending={busy}
                      responsive
                    />
                  </>
                )}
              </div>
            </>
          )
        )}

        {showSvcModal && (
          <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{svcEditId == null ? 'Nouveau service' : `Mettre à jour le service #${svcEditId}`}</h5>
                    <button type="button" className="btn-close" onClick={() => setShowSvcModal(false)} aria-label="Close"></button>
                  </div>
                  <form onSubmit={submitService}>
                    <div className="modal-body">
                      <div className="mb-2">
                        <label className="form-label">Libellé</label>
                        <input
                          type="text"
                          name="libelle_service"
                          value={svcForm.libelle_service}
                          onChange={(e) => setSvcForm({ libelle_service: e.target.value })}
                          className="form-control form-control-sm"
                          maxLength={60}
                          required
                        />
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary" onClick={() => setShowSvcModal(false)} disabled={busy}>Annuler</button>
                      <button type="submit" className="btn btn-primary" disabled={busy}>{svcEditId == null ? 'Créer' : 'Mettre à jour'}</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </>
        )}

        {showAssModal && (
          <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{assEditId == null ? 'Nouvelle association' : `Mettre à jour l'association #${assEditId}`}</h5>
                    <button type="button" className="btn-close" onClick={() => setShowAssModal(false)} aria-label="Close"></button>
                  </div>
                  <form onSubmit={submitAssoc}>
                    <div className="modal-body">
                      <div className="mb-2">
                        <label className="form-label">Prestation</label>
                        <Select
                          classNamePrefix="select"
                          isClearable
                          isSearchable
                          placeholder="Sélectionner la prestation"
                          options={prestations.map(p => ({ value: p.id, label: p.libelle_prestation }))}
                          value={(() => { const v = Number(assForm.id_prestation || 0); return prestations.length ? (prestations.map(p => ({ value: p.id, label: p.libelle_prestation }))).find(o => Number(o.value) === v) || null : null; })()}
                          onChange={(e) => setAssForm(prev => ({ ...prev, id_prestation: e ? e.value : "" }))}
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                          noOptionsMessage={() => 'Aucune prestation'}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Assureur</label>
                        <Select
                          classNamePrefix="select"
                          isClearable
                          isSearchable
                          placeholder="Sélectionner l'assureur"
                          options={assureurs.map(a => ({ value: a.id, label: a.libelle_assureur }))}
                          value={(() => { const v = Number(assForm.id_assureur || 0); return assureurs.length ? (assureurs.map(a => ({ value: a.id, label: a.libelle_assureur }))).find(o => Number(o.value) === v) || null : null; })()}
                          onChange={(e) => setAssForm(prev => ({ ...prev, id_assureur: e ? e.value : "" }))}
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                          noOptionsMessage={() => 'Aucun assureur'}
                        />
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary" onClick={() => setShowAssModal(false)} disabled={busy}>Annuler</button>
                      <button type="submit" className="btn btn-primary" disabled={busy}> {assEditId == null ? 'Créer' : 'Mettre à jour'} </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </>
        )}

        {showUsModal && (
          <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{usEditId == null ? 'Nouveau lien Utilisateur/Service' : `Mettre à jour le lien #${usEditId}`}</h5>
                    <button type="button" className="btn-close" onClick={() => setShowUsModal(false)} aria-label="Close"></button>
                  </div>
                  <form onSubmit={submitUserService}>
                    <div className="modal-body">
                      <div className="mb-2">
                        <label className="form-label">Rôle</label>
                        <Select
                          classNamePrefix="select"
                          isClearable
                          placeholder="Sélectionner le rôle"
                          options={[
                            { value: 'manager', label: 'Manager' },
                            { value: 'secretaire', label: 'Secrétaire' }
                          ]}
                          value={(() => {
                            const opts = [
                              { value: 'manager', label: 'Manager' },
                              { value: 'secretaire', label: 'Secrétaire' }
                            ];
                            return opts.find(o => o.value === (usForm.role || '')) || null;
                          })()}
                          onChange={(e) => setUsForm(prev => { const role = e ? e.value : ''; return { ...prev, role, recette: role === 'secretaire' ? 0 : prev.recette }; })}
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                          noOptionsMessage={() => 'Aucune option'}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Utilisateur</label>
                        <Select
                          classNamePrefix="select"
                          isClearable
                          isSearchable
                          placeholder="Sélectionner l'utilisateur"
                          options={users.map(u => ({ value: u.id, label: `${u.nom || ''} ${u.prenom || ''}`.trim() }))}
                          value={(() => { const v = Number(usForm.id_user || 0); return users.length ? (users.map(u => ({ value: u.id, label: `${u.nom || ''} ${u.prenom || ''}`.trim() }))).find(o => Number(o.value) === v) || null : null; })()}
                          onChange={(e) => setUsForm(prev => ({ ...prev, id_user: e ? e.value : "" }))}
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                          noOptionsMessage={() => 'Aucun utilisateur'}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label">Service</label>
                        <Select
                          classNamePrefix="select"
                          isClearable
                          isSearchable
                          placeholder="Sélectionner le service"
                          options={services.map(s => ({ value: s.id, label: s.libelle_service }))}
                          value={(() => { const v = Number(usForm.id_service || 0); return services.length ? (services.map(s => ({ value: s.id, label: s.libelle_service }))).find(o => Number(o.value) === v) || null : null; })()}
                          onChange={(e) => setUsForm(prev => ({ ...prev, id_service: e ? e.value : "" }))}
                          menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                          styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                          noOptionsMessage={() => 'Aucun service'}
                        />
                      </div>
                      {usForm.role === 'manager' && (
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="recetteSwitch"
                            checked={Number(usForm.recette) === 1}
                            onChange={(e) => setUsForm(prev => ({ ...prev, recette: e.target.checked ? 1 : 0 }))}
                          />
                          <label className="form-check-label" htmlFor="recetteSwitch">Accès recette</label>
                        </div>
                      )}
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary" onClick={() => setShowUsModal(false)} disabled={busy}>Annuler</button>
                      <button type="submit" className="btn btn-primary" disabled={busy}>{usEditId == null ? 'Créer' : 'Mettre à jour'}</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </>
        )}

        {showIndisModal && (
          <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Ouvrir une indisponibilité</h5>
                    <button type="button" className="btn-close" onClick={() => { setShowIndisModal(false); setIndisMotif(''); setIndisRowId(null); }} aria-label="Close"></button>
                  </div>
                  <form onSubmit={submitOpenIndis}>
                    <div className="modal-body">
                      <div className="mb-2">
                        <label className="form-label">Motif (30 caractères max)</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={indisMotif}
                          onChange={(e) => setIndisMotif(e.target.value)}
                          maxLength={30}
                          placeholder="Saisir un motif"
                          required
                        />
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn btn-secondary" onClick={() => { setShowIndisModal(false); setIndisMotif(''); setIndisRowId(null); }} disabled={busy}>Annuler</button>
                      <button type="submit" className="btn btn-primary" disabled={busy}>Valider</button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </>
        )}

        {errorMsg && (
          <>
            <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-modal="true">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Erreur</h5>
                    <button type="button" className="btn-close" onClick={() => setErrorMsg("")} aria-label="Close"></button>
                  </div>
                  <div className="modal-body">
                    <div className="alert alert-danger mb-0" role="alert" style={{ whiteSpace: 'pre-wrap' }}>{errorMsg}</div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setErrorMsg("")}>Fermer</button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-backdrop fade show"></div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShowStructure;